import { CONTACT_TYPE } from '@/constants/contactConstants';
import ContactService from '@/services/contactService';
import AgentService from '@/services/agentService';

const requestCrmContact = ({ commit, getters }, contactId) => {
  if (!contactId) {
    return null;
  }

  return ContactService.requestCrmContactDetails(contactId).then(({ data = {} }) => {
    commit('addCrmContact', data);
    return data;
  });
};

const requestCrmTemplate = ({ commit, getters }, { contactId, businessProcessId }) => {
  return ContactService.requestCrmContactTemplateForBusinessProcess(contactId, businessProcessId).then(response => {
    commit('addCrmTemplate', { contactId, businessProcessId, data: response.data });
    return response;
  });
};

const requestCrmContactBatch = ({ commit, getters }, contactIds) => {
  if (!contactIds || contactIds.length === 0) {
    return null;
  }

  return ContactService.requestCrmContactDetailsBatch(contactIds)
    .then(({ data }) => {
      data.forEach(contact => {
        commit('addCrmContact', contact);
      });
      return data;
    })
    .catch(console.log);
};

const updateCrmContact = ({ commit }, data) => {
  if (!data.contactId) {
    return Promise.reject(new Error('contactId is required'));
  }

  return ContactService.updateCrmContactProperties(data.contactId, data.properties).then(() => {
    commit('updateCrmContact', data);
  });
};

const removeCrmContacts = ({ commit }, contactIds) => {
  if (!contactIds) {
    return;
  }
  commit('removeCrmContact', contactIds);
};

const requestContacts = ({ commit, rootGetters }) => {
  return ContactService.requestContacts().then(response => {
    const contacts = response.data;

    const isLoggedInAgent = contact => contact.broadsoftIdentifer === rootGetters['AGENT/loggedInAgent'].hpbxExtension;
    contacts.enterprise = contacts.enterprise.filter(contact => !isLoggedInAgent(contact));
    commit('setContacts', contacts);

    return contacts;
  });
};

/**
 * Request main contacts.
 * Main contacts are agents, supervisors, channels and business processes.
 **/
const requestMainContacts = ({ commit, rootGetters }) => {
  return ContactService.requestMainContacts().then(response => {
    const contacts = response.data;

    commit('setContacts', contacts);

    return contacts;
  });
};

const requestCompanyContacts = ({ commit, rootGetters }) => {
  return ContactService.requestEnterpriseContacts().then(response => {
    const contacts = (response && response.data) || [];
    commit('setCompanyContacts', { contacts, getters: rootGetters });

    return contacts;
  });
};

const requestExternalContacts = ({ commit }) => {
  return ContactService.requestExternalContacts().then(response => {
    const contacts = response.data;

    commit('setExternalContacts', contacts);

    return contacts;
  });
};

const requestAgentState = ({ commit }, agent) => {
  if (!agent) {
    return;
  }

  const agentId = agent.agentId || agent.id;
  const contactType = agent.contactType;

  if (agentId && [CONTACT_TYPE.supervisor, CONTACT_TYPE.agent].includes(contactType)) {
    ContactService.requestAgentState(agentId).then(response => {
      const { id: stateId, name: stateName } = response.data;
      commit('updateAvailabilityState', { agentId, stateId, stateName });
    });
  }
};

const loadAgentStateBatch = ({ commit, rootGetters }) => {
  const loggedInAgentId = rootGetters['AGENT/loggedInAgent'].id;
  return AgentService.getAgentsCurrentState()
    .then(response => {
      response.data.forEach(({ agentId, stateId, stateName, stateChangeTimestamp }) => {
        commit('updateAvailabilityState', { agentId, stateId, stateName });
        if (loggedInAgentId === agentId) {
          commit('AGENT/updateLoggedInAgentState', { stateId, stateName }, { root: true });
        }
      });
    })
    .catch(console.log);
};

const requestFavoriteContacts = ({ commit, getters, rootGetters }, agentId) => {
  return ContactService.requestFavoriteContacts(agentId)
    .then(response => {
      if (response.data) {
        commit('setFavoriteContacts', response.data);
      }
    })
    .catch(console.log);
};

const addToFavoriteContacts = ({ getters, dispatch }, contact) => {
  const favoriteContacts = getters.getFavoriteContacts;
  const group = contact.isInternalContact ? 'internal' : 'external';

  if (favoriteContacts.favorites && favoriteContacts.favorites.length) {
    favoriteContacts.favorites.push({
      id: contact.id,
      contactTypeId: contact.contactType
    });
  } else {
    favoriteContacts[group].push(contact.id);
  }

  return dispatch('updateFavoriteContacts', favoriteContacts);
};

const removeFromFavoriteContacts = ({ getters, dispatch }, contact) => {
  const favoriteContacts = getters.getFavoriteContacts;
  const group = contact.isInternalContact ? 'internal' : 'external';

  if (favoriteContacts.favorites && favoriteContacts.favorites.length) {
    const favoriteIndex = favoriteContacts.favorites.findIndex(favorite => favorite.id === contact.id);
    if (favoriteIndex !== -1) {
      favoriteContacts.favorites.splice(favoriteIndex, 1);
      return dispatch('updateFavoriteContacts', favoriteContacts);
    }
  } else {
    const favoriteIndex = favoriteContacts[group].findIndex(favoriteId => favoriteId === contact.id);
    if (favoriteIndex !== -1) {
      favoriteContacts[group].splice(favoriteIndex, 1);
      return dispatch('updateFavoriteContacts', favoriteContacts);
    }
  }
};

const updateFavoriteContacts = ({ commit, rootGetters }, favoriteContacts) => {
  return ContactService.updateFavoriteContacts(rootGetters['AGENT/loggedInAgent'].id, favoriteContacts).then(response =>
    commit('setFavoriteContacts', response.data)
  );
};

const SOCKET_EVENT_AGENT_STATE_CHANGED_EVENT = ({ commit }, payload) => {
  commit('updateAvailabilityState', payload);
};

const SOCKET_EVENT_BROADSOFT_PRESENCE_CHANGED_EVENT = ({ commit }, payload) => {
  commit('updateAvailabilityState', payload);
};

export default {
  requestCrmContact,
  requestCrmTemplate,
  updateCrmContact,
  removeCrmContacts,
  requestContacts,
  requestMainContacts,
  requestAgentState,
  requestCompanyContacts,
  requestExternalContacts,
  requestFavoriteContacts,
  addToFavoriteContacts,
  removeFromFavoriteContacts,
  updateFavoriteContacts,
  loadAgentStateBatch,
  requestCrmContactBatch,
  SOCKET_EVENT_AGENT_STATE_CHANGED_EVENT,
  SOCKET_EVENT_BROADSOFT_PRESENCE_CHANGED_EVENT
};
